import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePart } from '@/hooks/usePartsQuery';
import { useCartStore } from '@/stores/cartStore';
import { categoryLabel } from '@/data/categories';
import { CURRENCIES, fx, type CurrencyCode } from '@/lib/currency';
import PhotoGallery from '@/components/inventory/PhotoGallery';
import AuthRecord from '@/components/inventory/AuthRecord';

export default function PartDetail() {
  const { partId } = useParams<{ partId: string }>();
  const { data: part, isLoading, error } = usePart(partId);
  const navigate = useNavigate();
  const currency = useCartStore((s) => s.currency);
  const setCurrency = useCartStore((s) => s.setCurrency);
  const addLine = useCartStore((s) => s.addLine);
  const lines = useCartStore((s) => s.lines);
  const [added, setAdded] = useState(false);

  if (isLoading) {
    return (
      <div className="app">
        <div className="main">
          <div className="empty-state">Loading part…</div>
        </div>
      </div>
    );
  }

  if (error || !part) {
    return (
      <div className="app">
        <div className="main">
          <div style={{ maxWidth: 520, margin: '60px auto', textAlign: 'center' }}>
            <div className="eyebrow">Part Not Found</div>
            <h1 className="h-display" style={{ fontSize: 48 }}>That casting number isn't on file.</h1>
            <p className="lead" style={{ margin: '0 auto 24px' }}>
              {error
                ? error instanceof Error ? error.message : String(error)
                : 'It may have been removed or marked as draft.'}
            </p>
            <Link to="/inventory" className="btn">← Back to Inventory</Link>
          </div>
        </div>
      </div>
    );
  }

  const inCart = lines.some((l) => l.partId === part.id);

  function handleAdd() {
    if (!part) return;
    addLine({
      partId: part.id,
      qty: 1,
      unitPrice: part.price,
      shippingWeight: part.weight,
      snapshot: {
        title: part.title,
        sku: part.sku,
        ...(part.casting !== undefined ? { casting: part.casting } : {}),
        iconId: part.iconId,
      },
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }

  return (
    <div className="app">
      <div className="main">
        <div style={{ marginBottom: 16 }}>
          <Link
            to="/inventory"
            style={{
              fontFamily: 'var(--mono)',
              fontSize: 11,
              letterSpacing: '0.18em',
              color: 'var(--text-dim)',
              textTransform: 'uppercase',
            }}
          >
            ← Back to Inventory
          </Link>
        </div>

        <div className="part-detail-grid">
          <div>
            <PhotoGallery
              photos={part.photos ?? []}
              iconFallback={part.iconId}
              altBase={part.title}
            />
          </div>

          <div className="part-detail-info">
            <div className="eyebrow">
              {categoryLabel(part.category)} · {part.type}
            </div>
            <h1 className="h-title" style={{ fontSize: 28, marginBottom: 14 }}>{part.title}</h1>

            <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <span className={`badge ${part.type === 'OEM' ? 'oem' : 'repro'}`}>{part.type}</span>
              <span className="badge live">{part.condition}</span>
              {part.status === 'reserved' && <span className="badge reserved">Reserved</span>}
            </div>

            <p style={{ color: 'var(--text-dim)', fontSize: 14, lineHeight: 1.55, marginBottom: 14 }}>
              {part.description}
            </p>

            <div className="modal-meta">
              <div>
                <span>Year</span>
                <span>{part.year}</span>
              </div>
              <div>
                <span>SKU</span>
                <span style={{ fontFamily: 'var(--mono)', color: 'var(--gold)' }}>{part.sku}</span>
              </div>
              <div>
                <span>Casting / P/N</span>
                <span style={{ fontFamily: 'var(--mono)' }}>{part.casting || '—'}</span>
              </div>
              <div>
                <span>Date Code</span>
                <span style={{ fontFamily: 'var(--mono)' }}>{part.dateCode || '—'}</span>
              </div>
              <div>
                <span>Ship Weight</span>
                <span>{part.weight} lbs</span>
              </div>
              <div>
                <span>Stock</span>
                <span>{part.stock} on hand</span>
              </div>
            </div>

            <AuthRecord auth={part.authentication} />

            <div className="eyebrow" style={{ marginTop: 18, marginBottom: 6 }}>Fits</div>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 22, lineHeight: 1.6 }}>
              {part.fitment.length > 0 ? part.fitment.join(' · ') : '—'}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 18,
                paddingTop: 18,
                borderTop: '1px solid var(--line)',
              }}
            >
              <div>
                <div className="part-price" style={{ fontSize: 36 }}>{fx(part.price, currency)}</div>
                <small
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10,
                    color: 'var(--text-muted)',
                    letterSpacing: '0.15em',
                  }}
                >
                  {currency} · before shipping
                </small>
              </div>
              <select
                value={currency}
                onChange={(e) => setCurrency(e.target.value as CurrencyCode)}
                style={{ padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '0.1em' }}
                aria-label="Currency"
              >
                {CURRENCIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                className="btn dark"
                onClick={() => navigate('/inventory')}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                ← Back
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleAdd}
                disabled={part.status === 'reserved'}
                style={{ flex: 1, justifyContent: 'center' }}
              >
                {added ? '✓ Added' : inCart ? '＋ Add Another' : '＋ Add to Quote'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
